import type { HttpClient } from '@wix/http-client'
import { keys } from '@wix/wix-data-client-common-standalone'
import type { FieldType } from '@wix/wix-data-schema-types'

interface AutomationsClient {
  reportFormEventToAutomationCreator: () => (
    payload: SubmissionDescriptor,
    collectionId: string,
    pageId: string,
    submitterId: string,
  ) => void
}

interface SubmissionDescriptor {
  detailedEventPayload: {
    [key: string]: {
      value: string
      keyName: string
      index: number
      valueType: FieldType
    }
  }
  eventUTCTime: Date
}

interface TriggerPayload {
  formId: string
  submissionTime: Date
  collectionId: string
  pageId: string
  submitterId: string
  fields: {
    [key: string]: {
      displayName: string
      value: string | number | boolean | Date | string[]
      type: FieldType
    }
  }
}

export const createAutomationsClient = (
  httpClient: HttpClient,
): AutomationsClient => {
  return {
    reportFormEventToAutomationCreator:
      () =>
        async (
          payload: SubmissionDescriptor,
          collectionId: string,
          pageId: string,
          submitterId: string,
        ): Promise<void> => {
          const formId = payload.detailedEventPayload['form-id'].value
          const submittedKeys = keys(payload.detailedEventPayload).filter(
            key => key !== 'form-id',
          ) as string[]

          const fieldValues = submittedKeys.reduce(
            (fieldValues, key) => {
            // TODO avoid this mangling by achieving the needed fieldKey format in https://github.com/wix-private/dbsm-viewer-app/blob/bcab344b31becc1c77555715a9377110e9fae146/dbsm-viewer-app/src/inverted-dependencies/helpers/automationUtils.js#L63 when specs.wixDataClient.AutomationsClientV2 and the broader Automations V2 is merged
              const fieldKey = key.split('.').join(':')
              const {
                keyName: displayName,
                valueType: type,
                value,
              } = payload.detailedEventPayload[key]
              fieldValues[fieldKey] = { displayName, value, type }
              return fieldValues
            },
            {} as TriggerPayload['fields'],
          )

          const triggerPayload: TriggerPayload = {
            formId,
            pageId,
            submissionTime: new Date(),
            submitterId,
            fields: fieldValues,
            collectionId,
          }

          await httpClient.post(
            '/_serverless/cms-automations/report-cms-form-submission',
            triggerPayload,
          )
        },
  }
}
